import Fuse from 'fuse.js'

import { Comment, Forum, ThreadWithAuthor, Doc, Thread } from './interfaces'
import { calendar, relative } from '@/helpers/time'
import { backend } from '@/services/backend'

/**
 * Thread data and logic to display thread in components.
 */
export class ThreadView<T extends Thread = ThreadWithAuthor> implements Thread {
  id: string
  url_slug: string
  title: string
  text: string
  author: T['author']
  created: string
  updated: string
  last_active: string
  comments: Comment[]
  comments_count: number
  notify_new_comment: boolean

  private _doc: Doc

  constructor(thread: T, doc: Doc) {
    this.id = thread.id
    this.url_slug = thread.url_slug
    this.title = thread.title
    this.text = thread.text
    this.author = thread.author
    this.created = thread.created
    this.updated = thread.updated
    this.last_active = thread.last_active
    this.comments_count = thread.comments_count
    this.comments = thread.comments || []
    this.notify_new_comment = thread.notify_new_comment

    this._doc = doc

    // Assign parent comments, we use them in the UI logic
    // to quickly access the reply's parent comment.
    for (const idx in this.comments) {
      const comment = this.comments[idx]
      for (const jdx in comment.replies) {
        comment.replies[jdx].parent = comment
      }
    }
    // @ts-ignore
  }

  get last_active_time(): string {
    return relative(this.last_active)
  }

  get posted_time(): string {
    return calendar(this.updated)
  }

  public async toggleNotifyNewComment(value: boolean): Promise<void> {
    const response = await backend.updateThreadNotifyNewComment(
      this,
      this._doc,
      value,
    )
    this.notify_new_comment = response.notify_new_comment
  }
}

/**
 * Forum data and logic to display forum in components.
 */
export class ForumView implements Forum {
  private _forum: Forum
  private _doc: Doc
  private _threads: ThreadView[]
  private _allThreads: ThreadView[]

  public id: string
  public title: string
  public notify_new_thread: boolean

  constructor(forum: Forum, doc: Doc) {
    this._forum = forum
    this._doc = doc
    this.id = forum.id
    this.title = forum.title
    this.notify_new_thread = forum.notify_new_thread

    this._threads = forum.threads.map((thread) => {
      return new ThreadView(thread, this._doc)
    })

    this._allThreads = this._threads
  }

  public async toggleNotifyNewThread(value: boolean): Promise<void> {
    const response = await backend.updateForumNotifyNewThread(
      this.id,
      value,
      this._doc,
    )
    this.notify_new_thread = response.notify_new_thread
  }

  /**
   * Forum threads.
   */
  get threads(): ThreadView[] {
    return this._threads
  }

  filter(search: string): void {
    search = search.trim()
    if (search === '') {
      this._threads = this._allThreads
      return
    }

    const options = {
      keys: ['title', 'author.username'],
    }
    const fuse = new Fuse(this._allThreads, options)
    const results = fuse.search(search)
    this._threads = results.map((r) => r.item)
  }
}
